/**
 * @file moneyStatus.ts
 * @brief One reading of a billed row's money for every web screen (18-10-2026):
 *        paid / part paid / unpaid / failed / refunded / part refunded /
 *        no charge, plus "Deleted" and "Disputed" markers.
 *
 * @details
 * Mirrors BACKEND shared/billing/moneyStatus.mjs. Reads the server's
 * `payment_status` when it is there (session detail), otherwise derives the
 * same answer from the invoices feed fields (`chargedCents`, `paymentFailed`,
 * `refundedCents`, `deleted`, `disputeStatus`). The amount to show is what was
 * collected when that differs from the bill.
 *
 * Night E2E F8 (21-10-2026): feed rows carry the server's `paymentStatus`,
 * read first. Two new states: `credit` (a negative plan invoice, a proration
 * credit: shown as a credit, never owed) and `included` (£0, the plan or pool
 * covered it). `partial` (part collected) already existed.
 *
 * Pure (no imports) so node:test can load it.
 *
 * @author DES
 * @date 18-10-2026
 */

export type MoneyTone = "success" | "warning" | "destructive" | "muted" | "info";

export type MoneyRowLike = {
  amount?: number | null;
  status?: string | null;
  /** The server's status for a feed row (21-10-2026). */
  paymentStatus?: string | null;
  billedCents?: number | null;
  chargedCents?: number | null;
  capturedCents?: number | null;
  paymentFailed?: boolean | null;
  refunded?: boolean | null;
  refundedCents?: number | null;
  deleted?: boolean | null;
  disputeStatus?: string | null;
  // session detail (snake_case, from moneyFields)
  payment_status?: string | null;
  billed_cents?: number | null;
  captured_cents?: number | null;
  refunded_cents?: number | null;
  payment_failed?: boolean | null;
  is_deleted?: boolean | null;
  dispute_status?: string | null;
};

export type MoneyView = {
  key: "paid" | "partial" | "open" | "failed" | "refunded" | "part_refunded" | "no_charge" | "included" | "credit";
  label: string;
  tone: MoneyTone;
  /** Collected, when it differs from the bill (else the bill). */
  shownCents: number;
  /** The bill, only when it differs from what is shown. */
  billedCents: number | null;
  refundedCents: number;
  deleted: boolean;
  /** Stripe dispute status, e.g. "needs_response", "won", "lost". */
  dispute: string | null;
};

const num = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const LABELS: Record<MoneyView["key"], { label: string; tone: MoneyTone }> = {
  paid: { label: "Paid", tone: "success" },
  partial: { label: "Part paid", tone: "warning" },
  open: { label: "Unpaid", tone: "warning" },
  failed: { label: "Failed", tone: "destructive" },
  refunded: { label: "Refunded", tone: "muted" },
  part_refunded: { label: "Part refunded", tone: "info" },
  no_charge: { label: "No charge", tone: "muted" },
  included: { label: "Included", tone: "muted" },
  credit: { label: "Credit", tone: "info" },
};

export function moneyStatus(row: MoneyRowLike): MoneyView {
  const billed = num(row.billed_cents) ?? num(row.billedCents) ?? num(row.amount) ?? 0;
  const capturedRaw = num(row.captured_cents) ?? num(row.chargedCents) ?? num(row.capturedCents);
  const legacyPaid = String(row.status ?? "").toLowerCase() === "paid";
  // Old feeds (before 00145) send only paid/open: treat paid as fully collected.
  const captured = capturedRaw ?? (legacyPaid ? billed : 0);
  const refunded = num(row.refunded_cents) ?? num(row.refundedCents) ?? (row.refunded ? captured : 0);
  const failed = !!(row.payment_failed ?? row.paymentFailed) && captured <= 0;

  let key: MoneyView["key"];
  const server = String(row.payment_status ?? row.paymentStatus ?? "");
  if (server in LABELS) key = server as MoneyView["key"];
  else if (refunded > 0 && captured > 0 && refunded >= captured) key = "refunded";
  else if (refunded > 0) key = "part_refunded";
  else if (failed || String(row.status ?? "").toLowerCase() === "failed") key = "failed";
  else if (billed <= 0 && captured <= 0) key = "no_charge";
  else if (captured <= 0) key = "open";
  else if (captured < billed) key = "partial";
  else key = "paid";

  // A credit is money back to the account: its size, labelled "Credit".
  if (key === "credit") {
    return {
      key,
      ...LABELS[key],
      shownCents: Math.abs(billed),
      billedCents: null,
      refundedCents: 0,
      deleted: !!(row.is_deleted ?? row.deleted),
      dispute: null,
    };
  }

  const shown = captured > 0 && captured !== billed ? captured : billed;
  return {
    key,
    ...LABELS[key],
    shownCents: shown,
    billedCents: shown !== billed ? billed : null,
    refundedCents: refunded,
    deleted: !!(row.is_deleted ?? row.deleted),
    dispute: (row.dispute_status ?? row.disputeStatus) || null,
  };
}

/** "Disputed", "Dispute won", "Dispute lost" for a Stripe dispute status. */
export function disputeLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  if (status === "won") return "Dispute won";
  if (status === "lost") return "Dispute lost";
  return "Disputed";
}
